<template>
    <div class="forward-chatbot-tunings">
        <div class="settings">
            <!-- Chatbot Seçimi Grubu -->
            <div class="setting-section">
                <div class="section-title">{{ $t("auto.selectChatbotToForward") }}</div>
                <div class="setting-row">
                    <div class="setting-group">
                        <div class="label">{{ $t("auto.chatbotScenario") }}</div>
                        <CustomPicker
                            v-model="config.storyId"
                            :items="stories"
                            idKey="_id"
                            labelKey="title"
                            :title="$t('auto.selectScenario')"
                            icon="smart_toy"
                        />
                    </div>

                    <div class="setting-group" v-if="config.storyId && !config.runAIFirstForce">
                        <div class="label">{{ $t("auto.startingBlock") }}</div>
                        <CustomPicker
                            v-model="config.initialBlockId"
                            :items="blocks"
                            idKey="id"
                            labelKey="name"
                            title="Blok seçin"
                            icon="account_tree"
                        />
                    </div>
                </div>
            </div>

            <a-divider />

            <!-- Yapay Zeka ve Genel Yönlendirme Ayarları -->
            <div class="settings-row">
                <!-- Yapay Zeka Ayarları Grubu -->
                <div class="setting-section">
                    <div class="section-title">{{ $t("auto.aiSettings") }}</div>
                    
                    <div class="setting-group">
                        <div class="switch-row">
                            <div class="label">{{ $t("auto.runAiFirst") }}</div>
                            <a-switch v-model="config.runAIFirstForce" />
                        </div>
                        <div class="description">
                            {{ $t("auto.aiWillRespondFirstAfterForwarding") }}
                        </div>
                    </div>

                    <div class="setting-group">
                        <div class="switch-row">
                            <div class="label">{{ $t("auto.forwardAllMessageHistory") }}</div>
                            <a-switch v-model="config.forwardAllMessagesToAI" />
                        </div>
                        <div class="description">
                            {{ $t("auto.allPreviousMessagesWillBeForwardedToAi") }}
                        </div>
                    </div>

                    <div class="setting-group" v-if="config.runAIFirstForce">
                        <div class="label">{{ $t("auto.aiTriggerMessage") }}</div>
                        <a-textarea
                            v-model="config.aiTriggerMessage"
                            :placeholder="$t('auto.firstMessageToSendToAi')"
                            :auto-size="{ minRows: 2, maxRows: 4 }"
                        />
                    </div>
                </div>

                <!-- Genel Yönlendirme Ayarları Grubu -->
                <div class="setting-section">
                    <div class="section-title">{{ $t("auto.generalForwardingSettings") }}</div>

                    <div class="setting-group">
                        <div class="switch-row">
                            <div class="label">{{ $t("auto.boomerangMode") }}</div>
                            <a-switch v-model="config.boomerangMode" />
                        </div>
                        <div class="description">
                            {{ $t("auto.willBeForwardedBackWhenChatbotCompletes") }}
                        </div>
                    </div>

                    <div class="setting-group">
                        <div class="label">{{ $t("auto.chatbotTriggerStartMessage") }}</div>
                        <a-textarea
                            v-model="config.chatbotTriggerMessage"
                            :placeholder="$t('auto.chatbotTriggerStartMessage')"
                            :auto-size="{ minRows: 2, maxRows: 4 }"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="actions">
            <a-button @click="$emit('cancel')">{{ $t("auto.cancel") }}</a-button>
            <a-button type="primary" @click="handleSend" :disabled="!isValid">{{ $t("auto.forward") }}</a-button>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { mapState } from "vuex";
import { CHATBOT_API } from "../../../../../../api/chatbotv2";

import log2 from "../../../../../../utils/log2";
import CustomPicker from "../../../../../pickers/CustomPicker.vue";
import flatDiagram from "../../../../../../views/Pages/Chatbot/utils/flatDiagram";

const log = log2("ForwardChatbotTunings");

export default Vue.extend({
    name: "ForwardChatbotTunings",
    components: { CustomPicker },
    props: {
        defaultStoryId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            config: {
                storyId: this.defaultStoryId,
                forwardAllMessagesToAI: false,
                runAIFirstForce: false,
                aiTriggerMessage: null,
                chatbotTriggerMessage: null,
                initialBlockId: null,
                boomerangMode: false,
            },
            diagram: null,
            blockSearchQuery: "",
        };
    },
    computed: {
        ...mapState({
            stories: (state) => state.CB.stories,
        }),
        blocks() {
            if (this.diagram) {
                const flattedDiagram = flatDiagram(this.diagram);
                const q = this.blockSearchQuery?.toLowerCase() || "";
                return flattedDiagram
                    .filter(
                        (block) =>
                            _.isEmpty(q) ||
                            (block.name || "").toLowerCase().includes(q) ||
                            block.type.toLowerCase().includes(q)
                    )
                    .map((x) => ({
                        id: x.id,
                        type: x.type,
                        name: x.name || x.type,
                    }));
            }
            return [];
        },
        isValid() {
            return this.config.storyId;
        },
    },
    methods: {
        handleBlockSearch(search) {
            this.blockSearchQuery = search;
        },
        handleSend() {
            this.$emit("send", this.config);
        },
        async fetchDraftBoard() {
            if (!this.config.storyId) return;

            try {
                const res = await CHATBOT_API.getStoryDraftDetail({
                    id: this.config.storyId,
                });
                const { data } = res.data;
                if (data) {
                    this.diagram = data.board.diagram;
                }
            } catch (error) {
                log("fetchDraftBoard error", error);
            }
        },
    },
    watch: {
        "config.storyId": {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.fetchDraftBoard();
                } else {
                    this.diagram = null;
                    this.config.initialBlockId = null;
                }
            },
        },
    },
});
</script>

<style lang="scss" scoped>
.forward-chatbot-tunings {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 10px;

    .settings {
        display: flex;
        flex-direction: column;
        gap: 25px;

        .settings-row {
            display: flex;
            gap: 15px;

            .setting-section {
                flex: 1;
                min-width: 0;
            }
        }

        .setting-section {
            display: flex;
            flex-direction: column;
            gap: 15px;
            padding: 15px;
            border: 1px solid #e8e8e8;
            border-radius: 8px;

            .section-title {
                font-size: 16px;
                font-weight: 600;
                color: #262626;
                margin-bottom: 5px;
            }

            .setting-row {
                display: flex;
                gap: 15px;

                .setting-group {
                    flex: 1;
                    min-width: 0;
                }
            }

            & + .ant-divider {
                margin: 5px 0;
            }
        }

        .setting-group {
            display: flex;
            flex-direction: column;
            gap: 5px;

            .switch-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            .label {
                font-size: 14px;
                font-weight: 500;
                color: #595959;
            }

            .description {
                font-size: 12px;
                color: #8c8c8c;
            }
        }
    }

    .actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }
}
</style>
